import React, { useState, useEffect } from 'react';
import { X, AlertCircle, Plus, Minus, Save, Loader2, Edit, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { Invoice, InvoiceItem, BillingService } from '@/types';

// Types imported from @/types

interface InvoiceEditModalProps {
  invoice: (Invoice & { items: InvoiceItem[] }) | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (invoiceData: any) => Promise<void>;
  services: BillingService[];
  loading?: boolean;
}

export default function InvoiceEditModal({
  invoice,
  isOpen,
  onClose,
  onSave,
  services,
  loading = false
}: InvoiceEditModalProps) {
  const [editedInvoice, setEditedInvoice] = useState<Invoice | null>(null);
  const [items, setItems] = useState<InvoiceItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Initialize data when invoice changes
  useEffect(() => {
    if (invoice && isOpen) {
      setEditedInvoice({ ...invoice });
      setItems([...invoice.items]);
      setError(null);
    }
  }, [invoice, isOpen]);

  // Calculate totals
  const subtotal = items.reduce((sum, item) => sum + item.total_price, 0);
  const discountAmount = editedInvoice?.discount_amount || 0;
  const taxAmount = editedInvoice?.tax_amount || 0;
  const totalAmount = subtotal - discountAmount + taxAmount;
  const balanceAmount = totalAmount - (editedInvoice?.paid_amount || 0);

  // Check if invoice can be edited
  const canEdit = editedInvoice?.payment_status !== 'Paid';
  const canEditItems = editedInvoice?.payment_status === 'Pending';

  if (!invoice || !editedInvoice) return null;

  const handleInputChange = (field: keyof Invoice, value: any) => {
    setEditedInvoice(prev => prev ? { ...prev, [field]: value } : null);
    setError(null);
  };

  const handleItemChange = (itemIndex: number, field: keyof InvoiceItem, value: any) => {
    setItems(prev => prev.map((item, index) => {
      if (index === itemIndex) {
        const updatedItem = { ...item, [field]: value };
        
        // Recalculate total price if quantity or unit price changes
        if (field === 'quantity' || field === 'unit_price') {
          updatedItem.total_price = updatedItem.quantity * updatedItem.unit_price;
        }
        
        return updatedItem;
      }
      return item;
    }));
    setError(null);
  };

  const removeItem = (itemIndex: number) => {
    if (!canEditItems) {
      setError('Cannot modify items for paid or partial invoices');
      return;
    }
    setItems(prev => prev.filter((_, index) => index !== itemIndex));
  };

  const addService = (service: BillingService) => {
    if (!canEditItems) {
      setError('Cannot modify items for paid or partial invoices');
      return;
    }
    
    const newItem: InvoiceItem = {
      id: `temp-${Date.now()}`,
      service_id: service.id,
      service_name: service.name,
      category: service.category,
      quantity: 1,
      unit_price: service.price,
      total_price: service.price,
    };
    
    setItems(prev => [...prev, newItem]);
  };

  const validateAndSave = async () => {
    // Validation
    if (!editedInvoice.patient_name.trim()) {
      setError('Patient name is required');
      return;
    }

    if (items.length === 0) {
      setError('Invoice must have at least one item');
      return;
    }

    if (items.some(item => !item.service_name.trim() || item.quantity <= 0 || item.unit_price < 0)) {
      setError('All items must have valid name, quantity, and price');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const invoiceData = {
        patient_name: editedInvoice.patient_name,
        patient_phone: editedInvoice.patient_phone || null,
        patient_address: editedInvoice.patient_address || null,
        date: editedInvoice.date,
        due_date: editedInvoice.due_date || null,
        items: items.map(item => ({
          service_id: item.service_id || null,
          service_name: item.service_name,
          category: item.category,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.total_price,
          notes: item.notes || null
        })),
        discount_amount: discountAmount,
        tax_amount: taxAmount,
        payment_method: editedInvoice.payment_method,
        payment_status: editedInvoice.payment_status,
        paid_amount: editedInvoice.paid_amount,
        notes: editedInvoice.notes || null,
        subtotal: subtotal,
        total_amount: totalAmount,
        balance_amount: balanceAmount
      };

      await onSave(invoiceData);
      onClose();
    } catch (error: any) {
      console.error('Failed to save invoice:', error);
      setError(error.message || 'Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-primary" />
              Edit Invoice - {invoice.invoice_number}
            </div>
            {!canEdit && (
              <Badge variant="outline" className="bg-warning/10 text-warning">
                Read Only (Paid)
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex">
          {/* Left Panel - Invoice Details */}
          <div className="flex-1 border-r p-4 space-y-4 overflow-y-auto">
            <h3 className="font-medium text-foreground">Invoice Information</h3>
            
            {/* Error Message */}
            {error && (
              <Alert className="status-critical">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Patient Information */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Patient Name *</label>
                <Input
                  value={editedInvoice.patient_name}
                  onChange={(e) => handleInputChange('patient_name', e.target.value)}
                  disabled={!canEdit}
                  placeholder="Enter patient name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Phone</label>
                <Input
                  value={editedInvoice.patient_phone || ''}
                  onChange={(e) => handleInputChange('patient_phone', e.target.value)}
                  disabled={!canEdit}
                  placeholder="Enter phone number"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Address</label>
              <Input
                value={editedInvoice.patient_address || ''}
                onChange={(e) => handleInputChange('patient_address', e.target.value)}
                disabled={!canEdit}
                placeholder="Enter address"
              />
            </div>

            {/* Payment Information */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Payment Method</label>
                <Select
                  value={editedInvoice.payment_method}
                  onValueChange={(value) => handleInputChange('payment_method', value)}
                  disabled={!canEdit}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="UPI">UPI</SelectItem>
                    <SelectItem value="Card">Card</SelectItem>
                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                    <SelectItem value="Cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Payment Status</label>
                <Select
                  value={editedInvoice.payment_status}
                  onValueChange={(value) => handleInputChange('payment_status', value)}
                  disabled={!canEdit}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Partial">Partial</SelectItem>
                    <SelectItem value="Paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Financial Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Discount Amount</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={discountAmount}
                  onChange={(e) => handleInputChange('discount_amount', parseFloat(e.target.value) || 0)}
                  disabled={!canEdit}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Paid Amount</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editedInvoice.paid_amount}
                  onChange={(e) => handleInputChange('paid_amount', parseFloat(e.target.value) || 0)}
                  disabled={!canEdit}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Notes</label>
              <Input
                value={editedInvoice.notes || ''}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                disabled={!canEdit}
                placeholder="Add any notes..."
              />
            </div>

            {/* Invoice Items */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-foreground">Invoice Items</h3>
                {canEditItems && (
                  <Button size="sm" variant="outline" onClick={() => setError('Select a service from the right panel to add')}>
                    <Plus className="w-3 h-3 mr-1" />
                    Add Item
                  </Button>
                )}
              </div>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {items.map((item, index) => (
                  <div key={index} className="border rounded-lg p-3 bg-muted/30">
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-4">
                        <Input
                          value={item.service_name}
                          onChange={(e) => handleItemChange(index, 'service_name', e.target.value)}
                          disabled={!canEditItems}
                          placeholder="Service name"
                          className="text-sm"
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
                          disabled={!canEditItems}
                          className="text-sm"
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unit_price}
                          onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                          disabled={!canEditItems}
                          className="text-sm"
                        />
                      </div>
                      <div className="col-span-2">
                        <div className="text-sm font-medium text-center">
                          ₹{((item.total_price) || 0).toFixed(2)}
                        </div>
                      </div>
                      <div className="col-span-2">
                        {canEditItems && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeItem(index)}
                            className="w-full text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals Summary */}
            <div className="border-t pt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span>Subtotal:</span>
                <span>₹{subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Discount:</span>
                <span>-₹{discountAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Tax:</span>
                <span>₹{taxAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-semibold border-t pt-2">
                <span>Total:</span>
                <span>₹{totalAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Paid:</span>
                <span>₹{(editedInvoice.paid_amount || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-medium text-warning">
                <span>Balance:</span>
                <span>₹{balanceAmount.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Right Panel - Available Services */}
          {canEditItems && (
            <div className="w-80 p-4 bg-muted/10">
              <h3 className="font-medium text-foreground mb-3">Available Services</h3>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {services.filter(s => s.is_active).map(service => (
                  <div
                    key={service.id}
                    onClick={() => addService(service)}
                    className="p-3 border rounded-lg cursor-pointer hover:bg-primary/5 hover:border-primary/30 transition-colors"
                  >
                    <div className="font-medium text-sm">{service.name}</div>
                    <div className="text-xs text-muted-foreground">{service.category}</div>
                    <div className="text-sm font-medium text-primary">₹{service.price}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          {canEdit && (
            <Button onClick={validateAndSave} disabled={saving || loading}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}